import { Link } from "react-router-dom";
import { VideosButton } from "@/components/layout/VideosButton";
import { InstallBanner } from "@/components/pwa/InstallApp";
import { ArrowRight, BadgeCheck, RefreshCcw, Smartphone } from "lucide-react";
import { Wordmark } from "@/components/brand/Wordmark";
import { AppGrid } from "@/components/apps/AppGrid";
import { usePublicApps } from "@/hooks/usePublicApps";
import { MinisiteSection } from "@/components/apps/MinisiteSection";
import { LatestAddition } from "@/components/apps/LatestAddition";
import { useSite } from "@/context/SiteContext";
import { useI18n } from "@/i18n/I18nContext";
import { DevPanel } from "@/components/market/DevPanel";
import { ThirdPartyEntry } from "@/components/market/ThirdPartyEntry";
import { CustomPlanCta } from "@/components/apps/PlanNotes";
import { NewsPushButton } from "@/components/pwa/NewsPush";
import { AndroidIcon, AppleIcon } from "@/components/apps/Platform";

const FEATURES = [
  { icon: BadgeCheck, key: "home.f1" },
  { icon: RefreshCcw, key: "home.f2" },
  { icon: Smartphone, key: "home.f3", both: true },
];

const BothIcon = () => (
  <span className="relative h-7 w-7 shrink-0 text-[var(--gold-bright)]" aria-hidden="true">
    <Smartphone className="h-7 w-7" />
    <span className="absolute -bottom-1 -end-2 flex gap-0.5 rounded-full bg-black/80 px-1 py-0.5"><AndroidIcon className="h-3 w-3" /><AppleIcon className="h-3 w-3" /></span>
  </span>
);

export default function Home() {
  const { apps, error } = usePublicApps();
  const { logoUrl } = useSite();
  const { t } = useI18n();
  return (
    <div data-testid="home-page">
      <section className="relative mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-8 sm:pt-24">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <p className="fade-up font-display text-[0.7rem] uppercase tracking-[0.35em] text-[var(--red-bright)]">
              {t("home.eyebrow")}
            </p>
            <Wordmark size="hero" testId="hero-wordmark" className="fade-up mt-5" />
            <div className="gold-line mt-8 max-w-xl" />
            <p className="fade-up mt-6 max-w-xl text-sm text-neutral-300 md:text-lg" style={{ animationDelay: "120ms" }} data-testid="home-tagline">
              {t("home.tagline")}
            </p>
            <div className="fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: "200ms" }}>
              <Link to="/apps" className="btn-gold" data-testid="hero-catalogue-btn">
                {t("home.cta")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
              </Link>
            </div>
            <NewsPushButton className="fade-up mt-4" testId="home-news-push-btn" />
          </div>
          <img
            src={logoUrl}
            alt="Logo FAB-MIND-OS"
            className="fade-up hidden h-56 w-56 shrink-0 rounded-[2.5rem] object-cover ring-2 ring-[rgba(245,197,66,0.55)] shadow-[0_0_80px_-10px_rgba(208,20,31,0.8)] lg:block xl:h-64 xl:w-64"
            data-testid="hero-logo"
          />
        </div>
      </section>

      <div className="mx-auto -mt-8 mb-10 flex max-w-7xl px-4 sm:px-8"><VideosButton testId="home-videos-cta" /></div>
      <InstallBanner />
      <section className="mx-auto max-w-7xl px-4 sm:px-8" data-testid="home-features">
        <div className="metal-red grid gap-6 rounded-2xl p-6 sm:grid-cols-3 sm:p-8">
          {FEATURES.map(({ icon: Icon, key, both }) => (
            <div key={key} className="flex min-w-0 gap-4" data-testid={`home-feature-${key.slice(5)}`}>
              {both ? <BothIcon /> : <Icon className="h-7 w-7 shrink-0 text-[var(--gold-bright)]" />}
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-sm font-bold uppercase tracking-wider gold-text hyphens-none [overflow-wrap:break-word]">{t(`${key}.title`)}</h2>
                <p className="mt-1 text-sm text-red-100/80">{t(`${key}.text`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-8" data-testid="home-apps-section">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-display text-[0.7rem] uppercase tracking-[0.3em] text-[var(--red-bright)]">{t("home.catalogue")}</p>
            <h2 className="mt-2 font-display text-base font-bold uppercase tracking-wider text-white md:text-lg">
              {t("home.appsTitle")}
            </h2>
          </div>
          <Link to="/apps" className="text-sm font-semibold text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="home-see-all-link">
            {t("home.seeAll")}
          </Link>
        </div>
        <div className="mb-8 space-y-3">
          <CustomPlanCta slim testId="home-custom-plan" />
          <ThirdPartyEntry slim testId="home-third-party-entry" />
          <DevPanel slim testId="home-dev-panel" />
        </div>
        <LatestAddition apps={apps} />
        <AppGrid apps={apps && apps.filter((a) => a.category !== "minisite")} error={error} />
      </section>
      <MinisiteSection apps={apps?.filter((a) => a.category === "minisite")} />
    </div>
  );
}
